import { isBoolean, isString } from './main';
import { isDefined, isNil } from './maybe';

function isNumber(value: unknown): value is number {
  return isDefined(value) && typeof value === 'number';
}

function isInteger(value: unknown): value is number {
  return isNumber(value) && value % 1 === 0;
}

function isStringInteger(value: string): boolean {
  return /^[+-]?[\d]+$/.test(value);
}

function isFloat(value: unknown): value is number {
  return isNumber(value) && value % 1 !== 0;
}

function isStringFloat(value: string): boolean {
  return /^[+-]?([\d]*[.])?[\d]+$/.test(value);
}

function isStringNumber(value: string): boolean {
  return isStringInteger(value) || isStringFloat(value);
}

/**
 * Rounding to the nearest number with a given number of decimal places
 */
function round(value: number, decimals: number = 0): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

/**
 * Rounding up to the nearest number with a given number of decimal places
 */
function floor(value: number, decimals: number = 0): number {
  const factor = Math.pow(10, decimals);
  return Math.floor(value * factor) / factor;
}

/**
 * Rounding down to the nearest number with a given number of decimal places
 */
function ceil(value: number, decimals: number = 0): number {
  const factor = Math.pow(10, decimals);
  return Math.ceil(value * factor) / factor;
}

function clamp(value: number | string | boolean, min?: number, max?: number): number;
function clamp(value: number | string | boolean | undefined | null, min?: number, max?: number): number | undefined;
function clamp(
  value: number | string | boolean | undefined | null,
  min: number = 0,
  max: number = 100
): number | undefined {
  if (isNil(value)) {
    return;
  }

  if (isNumber(value)) {
    const numberValue = round(value);

    return Math.max(min, Math.min(max, numberValue));
  }

  if (isString(value)) {
    const float = parseFloat(value.replace(',', '.'));
    const int = parseInt(value, 10);

    const parsed = isNaN(float) ? int : float;

    if (!isNaN(parsed)) {
      const numberValue = round(parsed);

      return Math.max(min, Math.min(max, numberValue));
    }

    return 0;
  }

  if (isBoolean(value)) {
    return value ? 100 : 0;
  }

  return 0;
}

export { isNumber, isInteger, isStringInteger, isFloat, isStringFloat, isStringNumber, round, floor, ceil, clamp };
